import { useState, useRef, DragEvent, ChangeEvent } from "react";
import { supabase } from "../../../lib/supabase";

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  required?: boolean;
}

const BUCKET = "news-images";
const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_EXT = ".jpg, .jpeg, .png, .webp, .gif";

function generateFileName(file: File): string {
  const ext = file.name.split(".").pop() ?? "jpg";
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `${ts}-${rand}.${ext}`;
}

export default function ImageUploader({ value, onChange, required }: ImageUploaderProps) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Unsupported format. Use: ${ALLOWED_EXT}`;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File too large. Max ${MAX_SIZE_MB}MB.`;
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setUploading(true);
    setProgress(10);

    const fileName = generateFileName(file);

    // Simulate progress during upload
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev < 85 ? prev + 15 : prev));
    }, 200);

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(fileName, file, { cacheControl: "3600", upsert: false });

    clearInterval(progressInterval);

    if (uploadError) {
      setError(`Upload failed: ${uploadError.message}`);
      setUploading(false);
      setProgress(0);
      return;
    }

    setProgress(100);

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
    onChange(data.publicUrl);

    setTimeout(() => {
      setUploading(false);
      setProgress(0);
    }, 400);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    // reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleRemove = async () => {
    // Try to delete from storage if it's a storage URL
    if (value.includes(`/storage/v1/object/public/${BUCKET}/`)) {
      const parts = value.split(`/storage/v1/object/public/${BUCKET}/`);
      if (parts[1]) {
        await supabase.storage.from(BUCKET).remove([parts[1]]);
      }
    }
    onChange("");
    setError("");
  };

  // Filled state — image already uploaded
  if (value && !uploading) {
    return (
      <div className="relative rounded-lg overflow-hidden border border-cool-gray-200 group">
        <div className="h-48 w-full">
          <img
            src={value}
            alt="Cover"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-navy text-xs font-semibold rounded-md hover:bg-cool-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line" />
            </span>
            Replace
          </button>
          <button
            type="button"
            onClick={handleRemove}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white text-xs font-semibold rounded-md hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-delete-bin-line" />
            </span>
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    );
  }

  // Uploading state
  if (uploading) {
    return (
      <div className="rounded-lg border border-cool-gray-200 bg-cool-gray-50 h-48 flex flex-col items-center justify-center gap-4 px-6">
        <div className="w-10 h-10 flex items-center justify-center text-navy/40">
          <i className="ri-upload-cloud-2-line text-3xl animate-pulse" />
        </div>
        <div className="w-full max-w-xs">
          <div className="flex justify-between text-xs text-navy/50 mb-1.5">
            <span>Uploading…</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-cool-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-navy rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Empty / drop zone state
  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-lg border-2 border-dashed h-48 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-200 ${
          dragging
            ? "border-navy bg-navy/5 scale-[1.01]"
            : "border-cool-gray-300 bg-cool-gray-50 hover:border-navy/40 hover:bg-cool-gray-100"
        }`}
      >
        <div className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${dragging ? "bg-navy/10" : "bg-white border border-cool-gray-200"}`}>
          <i className={`ri-image-add-line text-xl transition-colors ${dragging ? "text-navy" : "text-navy/40"}`} />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-navy/70">
            {dragging ? "Drop image here" : "Drag & drop or click to upload"}
          </p>
          <p className="text-xs text-navy/35 mt-1">
            JPG, PNG, WebP, GIF · max {MAX_SIZE_MB}MB
          </p>
        </div>

        {required && !value && (
          <input
            type="text"
            required
            value=""
            readOnly
            tabIndex={-1}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
            aria-hidden="true"
          />
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-red-50 border border-red-100 rounded-md">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-500 text-sm" />
          </span>
          <p className="text-xs text-red-600 font-medium">{error}</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(",")}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
